import { ReactNode } from 'react';
import { Box, Container, Tab, Tabs } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';

interface AdminLayoutProps {
  children: ReactNode;
}

/**
 * AdminLayout - Consistent layout wrapper for all admin pages
 *
 * Provides:
 * - Admin navigation tabs
 * - Consistent spacing and styling
 * - Admin-specific theming
 */
export default function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine current tab based on pathname
  const currentTab = location.pathname.split('/')[2] || 'dashboard';

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    navigate(`/admin/${newValue}`);
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Admin Navigation Tabs */}
      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          position: 'sticky',
          top: 0,
          zIndex: 1100,
        }}
      >
        <Container maxWidth="xl">
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minWidth: 100,
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 500,
              },
            }}
          >
            <Tab label="Dashboard" value="dashboard" />
            <Tab label="Users" value="users" />
            <Tab label="Businesses" value="businesses" />
            <Tab label="Content" value="content" />
            <Tab label="Analytics" value="analytics" />
            <Tab label="System" value="system" />
            <Tab label="Billing" value="billing" />
            <Tab label="Tools" value="tools" />
          </Tabs>
        </Container>
      </Box>

      {/* Main Content */}
      <Box sx={{ py: 4 }}>
        {children}
      </Box>
    </Box>
  );
}
